import { useAccount, useBalance, useReadContract, useReadContracts, useWriteContract, useWaitForTransactionReceipt } from "wagmi";
import { useEffect, useMemo } from "react";
import type { Address } from "viem";
import { maxUint256 } from "viem";
import { DEX_ROUTER_ABI, PIGGY_TOKEN_ABI } from "@/lib/abis";
import { ADDRESSES } from "@/lib/config";

const ZERO: Address = "0x0000000000000000000000000000000000000000";

export type SwapDirection = "buy" | "sell";

// PIGGY transfer tax — fires when either side of the transfer is a registered
// pair (i.e. every swap leg through Uniswap). The router is
// fee-on-transfer-aware, but its getAmountsOut quote does NOT subtract the
// tax — that has to happen client-side. The rate is read live from the token
// because the SELL tax is elevated during the 48h launch glide (8% then 6%);
// hardcoding 4% would over-quote sells during glide and produce a too-high
// minOut that reverts or weakens slippage protection.
const FALLBACK_TAX_BPS = 400n;
const BPS_DENOM = 10_000n;

/// Live tax (bps) for the given swap direction, read from the PIGGY token.
/// Falls back to the steady-state 4% if the read is unavailable.
export function useCurrentTaxBps(direction: SwapDirection): bigint {
    const piggy = ADDRESSES.PIGGY_TOKEN;
    const { data } = useReadContract({
        address: piggy,
        abi: PIGGY_TOKEN_ABI,
        functionName: direction === "sell" ? "currentSellTaxBps" : "currentBuyTaxBps",
        query: { enabled: Boolean(piggy), refetchInterval: 30_000 },
    });
    return data !== undefined ? BigInt(data as number) : FALLBACK_TAX_BPS;
}

/**
 * Quote a swap of `amountIn` ETH↔PIGGY using the Uniswap router's
 * getAmountsOut, with tax-corrected amountIn for sells.
 *
 * For BUY: the router receives full ethIn (no tax on ETH) and returns the
 * gross PIGGY amount; tax fires when the PIGGY then leaves the pair to the
 * user, reducing the user's received amount by 4%.
 *
 * For SELL: tax fires when the user's PIGGY enters the pair, so the router
 * effectively only sees `piggyIn * 0.96`. Quoting with the gross amountIn
 * over-estimates by ~4% and produces a too-high minOut, weakening slippage
 * protection. We quote with the effective post-tax amountIn instead.
 * (Audit fix H8.)
 */
export function useSwapQuote(direction: SwapDirection, amountIn: bigint) {
    const router = ADDRESSES.DEX_ROUTER;
    const wnative   = ADDRESSES.WNATIVE;
    const piggy  = ADDRESSES.PIGGY_TOKEN;

    const taxBps = useCurrentTaxBps(direction);

    const path = useMemo<readonly Address[]>(() => {
        if (!wnative || !piggy) return [];
        return direction === "buy" ? [wnative, piggy] : [piggy, wnative];
    }, [direction, wnative, piggy]);

    // Sell: shrink amountIn by the LIVE tax so the quote reflects what the
    // router actually receives. Buy: tax applies at the output, not the input.
    const effectiveIn = direction === "sell"
        ? (amountIn * (BPS_DENOM - taxBps)) / BPS_DENOM
        : amountIn;

    const enabled = Boolean(router && wnative && piggy && effectiveIn > 0n && path.length === 2);
    const { data, isLoading } = useReadContract({
        address: router,
        abi: DEX_ROUTER_ABI,
        functionName: "getAmountsOut",
        args: enabled ? [effectiveIn, [...path]] : undefined,
        query: { enabled, refetchInterval: 12_000 },
    });

    const amountOut = ((data as bigint[] | undefined)?.[1]) ?? 0n;
    // taxBps is returned so callers can tax-correct the BUY-side output too.
    return { amountOut, taxBps, isLoading };
}

const PAIR_ABI = [
    { type: "function", name: "getReserves", stateMutability: "view", inputs: [],
      outputs: [{ type: "uint112" }, { type: "uint112" }, { type: "uint32" }] },
    { type: "function", name: "token0", stateMutability: "view", inputs: [], outputs: [{ type: "address" }] },
] as const;

/// Live pool reserves oriented as { piggy, weth }. Used to size auto-slippage
/// from a trade's price impact. Zeroes until the read resolves.
export function usePairReserves(): { piggy: bigint; weth: bigint } {
    const pair = ADDRESSES.PIGGY_PAIR;
    const piggy = ADDRESSES.PIGGY_TOKEN;
    const { data } = useReadContracts({
        allowFailure: true,
        contracts: pair && piggy
            ? [
                  { address: pair, abi: PAIR_ABI, functionName: "getReserves" } as const,
                  { address: pair, abi: PAIR_ABI, functionName: "token0" } as const,
              ]
            : undefined,
        query: { enabled: Boolean(pair && piggy), refetchInterval: 12_000 },
    });
    const reserves = data?.[0]?.result as readonly [bigint, bigint, number] | undefined;
    const token0 = data?.[1]?.result as string | undefined;
    if (!reserves || !token0 || !piggy) return { piggy: 0n, weth: 0n };
    const piggyIsToken0 = token0.toLowerCase() === piggy.toLowerCase();
    return {
        piggy: piggyIsToken0 ? reserves[0] : reserves[1],
        weth: piggyIsToken0 ? reserves[1] : reserves[0],
    };
}

/// Recommended slippage (bps) for a trade, sized from its live price impact so
/// swaps don't revert on a thin pool and the user never has to think about it.
/// The tax is already handled in the quote (expectedOut is net-of-tax), so this
/// only needs to cover the impact-correlated price drift between quote and
/// execution: 3% base + half the trade's price impact, clamped to [3%, 15%].
export function autoSlippageBps(params: {
    direction: SwapDirection;
    amountIn: bigint;
    grossOut: bigint; // pre-tax getAmountsOut result
    taxBps: bigint;
    reserves: { piggy: bigint; weth: bigint };
}): number {
    const { direction, amountIn, grossOut, taxBps, reserves } = params;
    const BASE = 300n, FLOOR = 300n, CEIL = 1500n;
    if (amountIn === 0n || grossOut === 0n) return Number(BASE);
    const reserveIn = direction === "buy" ? reserves.weth : reserves.piggy;
    const reserveOut = direction === "buy" ? reserves.piggy : reserves.weth;
    if (reserveIn === 0n || reserveOut === 0n) return 500; // reserves not loaded: safe 5%
    // Input the pool actually receives (sell tax shrinks it; buy tax is on output).
    const effIn = direction === "sell" ? (amountIn * (BPS_DENOM - taxBps)) / BPS_DENOM : amountIn;
    const noImpactOut = (effIn * reserveOut) / reserveIn; // ideal, ignoring fee + impact
    let impactBps = 0n;
    if (noImpactOut > grossOut) impactBps = ((noImpactOut - grossOut) * BPS_DENOM) / noImpactOut;
    let auto = BASE + impactBps / 2n;
    if (auto < FLOOR) auto = FLOOR;
    if (auto > CEIL) auto = CEIL;
    return Number(auto);
}

export function useBalances() {
    const { address } = useAccount();
    const piggy = ADDRESSES.PIGGY_TOKEN;
    const user: Address = address ?? ZERO;

    const eth = useBalance({ address, query: { enabled: Boolean(address), refetchInterval: 12_000 } });

    const routerForAllow: Address = ADDRESSES.DEX_ROUTER ?? ZERO;
    const piggyReads = useReadContracts({
        allowFailure: true,
        contracts: piggy && address
            ? [
                  { address: piggy, abi: PIGGY_TOKEN_ABI, functionName: "balanceOf", args: [user] } as const,
                  { address: piggy, abi: PIGGY_TOKEN_ABI, functionName: "allowance", args: [user, routerForAllow] } as const,
              ]
            : undefined,
        query: { enabled: Boolean(piggy && address && ADDRESSES.DEX_ROUTER), refetchInterval: 12_000 },
    });

    return {
        eth: eth.data?.value ?? 0n,
        piggy: (piggyReads.data?.[0]?.result as bigint | undefined) ?? 0n,
        allowanceToRouter: (piggyReads.data?.[1]?.result as bigint | undefined) ?? 0n,
    };
}

function useTxStatus() {
    const { writeContract, data: hash, isPending, reset } = useWriteContract();
    const { isLoading: confirming, isSuccess: confirmed } = useWaitForTransactionReceipt({ hash });
    useEffect(() => {
        if (confirmed) {
            const t = setTimeout(reset, 4_000);
            return () => clearTimeout(t);
        }
    }, [confirmed, reset]);
    return {
        writeContract, hash,
        status: confirmed ? "confirmed" : confirming ? "confirming" : isPending ? "pending" : "idle",
    } as const;
}

export function useApprovePiggyForRouter() {
    const piggy = ADDRESSES.PIGGY_TOKEN;
    const router = ADDRESSES.DEX_ROUTER;
    const tx = useTxStatus();
    const approve = () => {
        if (!piggy || !router) return;
        tx.writeContract({
            address: piggy,
            abi: PIGGY_TOKEN_ABI,
            functionName: "approve",
            args: [router, maxUint256],
        });
    };
    return { approve, ...tx };
}

export function useSwap() {
    const { address } = useAccount();
    const router = ADDRESSES.DEX_ROUTER;
    const wnative = ADDRESSES.WNATIVE;
    const piggy = ADDRESSES.PIGGY_TOKEN;
    const tx = useTxStatus();

    const buy = (ethIn: bigint, minPiggyOut: bigint) => {
        if (!router || !wnative || !piggy || !address) return;
        const deadline = BigInt(Math.floor(Date.now() / 1000) + 300);
        tx.writeContract({
            address: router,
            abi: DEX_ROUTER_ABI,
            functionName: "swapExactETHForTokensSupportingFeeOnTransferTokens",
            args: [minPiggyOut, [wnative, piggy], address, deadline],
            value: ethIn,
        });
    };

    const sell = (piggyIn: bigint, minEthOut: bigint) => {
        if (!router || !wnative || !piggy || !address) return;
        const deadline = BigInt(Math.floor(Date.now() / 1000) + 300);
        tx.writeContract({
            address: router,
            abi: DEX_ROUTER_ABI,
            functionName: "swapExactTokensForETHSupportingFeeOnTransferTokens",
            args: [piggyIn, minEthOut, [piggy, wnative], address, deadline],
        });
    };

    return { buy, sell, ...tx };
}
